import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { IndexCard } from '../../../../../auth/store/models';
import { SagView } from '../../../cases/models';
import { TimeBudget } from '../../../../../timetrack/store/models';
import { AlertService } from '../../../../../core/alerts/alert.service';
import { ResourceplanTableHelperService } from './resourceplan-table-helper.service';
import { LuxonUtils } from '@shared';
import { DateTime } from 'luxon';

@Component({
    selector: 'app-resourceplan-table',
    templateUrl: './resourceplan-table.component.html',
    styleUrls: ['./resourceplan-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ResourceplanTableComponent {
    @Input() caseUsers: IndexCard[];
    @Input() la: number;
    @Input() ka: number;
    @Input() sag: SagView;
    @Input() timeBudgets: TimeBudget[];
    @Output() createTimeBudget = new EventEmitter<TimeBudget>();
    @Output() updateTimeBudget = new EventEmitter<TimeBudget>();
    @Output() deleteTimeBudget = new EventEmitter<number>();
    @Output() updateClientCaseV2 = new EventEmitter<{sag: SagView, partialSag: Partial<SagView>}>();

    @ViewChild('prisjusteringInput') prisjusteringInput: ElementRef;

    get caseMonths(): Date[] {
        return this.resourceplanTableHelper.caseMonths(this.sag);
    }

    get totalTimer(): number {
        return this.resourceplanTableHelper.totalTimer(this.timeBudgets);
    }

    get totalHonorar(): number {
        return this.resourceplanTableHelper.totalHonorar(this.timeBudgets);
    }

    get timerOutsidePeriod(): boolean {
        if (this.timeBudgets?.length < 1) { return false; }
        const sagFraDate = LuxonUtils.fromIso(this.sag?.fra);
        const sagTilDate = LuxonUtils.fromIso(this.sag?.til);
        return this.isOutsideTimerbudgets(sagFraDate, sagTilDate);
    }

    constructor(
        public resourceplanTableHelper: ResourceplanTableHelperService,
        private alert: AlertService,
    ) { }

    isOutsideTimerbudgets(fraDate: DateTime, tilDate: DateTime): boolean {
        const sortedTimeBudgets = this.timeBudgets?.filter(tb => tb.timer !== 0)
            .sort((a, b) => a.mth - b.mth)
            .sort((a, b) => a.aar - b.aar);
        if (!sortedTimeBudgets || sortedTimeBudgets.length < 1) { return; }
        const firstTimeBudgets = sortedTimeBudgets[0];
        const lastTimeBudgets = sortedTimeBudgets[sortedTimeBudgets.length - 1];
        const firstBudgetDate = LuxonUtils.fromObject({year: firstTimeBudgets.aar, month: firstTimeBudgets.mth, day: 1});
        const lastBudgetDate = LuxonUtils.fromObject({year: lastTimeBudgets.aar, month: lastTimeBudgets.mth, day: 1});
        return firstBudgetDate < fraDate.startOf('month') || lastBudgetDate > tilDate.endOf('month');
    }

    saveTimer(timer: number, date: Date, user: IndexCard) {
        const mth = date.getMonth() + 1;
        const aar = date.getFullYear();
        const medID = user.cardId;
        const existingTimeBudget = this.timeBudgets?.find(tb => tb.mth === mth && tb.aar === aar && tb.medID === medID);
        const timesats = user['timesats' + this.sag.timetakst] ?? existingTimeBudget.timesats;
        if (existingTimeBudget) {
            const updateTimeBudget = {
                ...existingTimeBudget,
                timer,
                timesats
            };
            if (timer === 0) {
                this.deleteTimeBudget.emit(existingTimeBudget.oid);
            } else {
                this.updateTimeBudget.emit(updateTimeBudget);
            }
        } else {
            if (!timer) { return; }
            const newTimeBudget = {
                oid: 0,
                sagID: this.sag.pid,
                medID,
                mth,
                aar,
                timer,
                timesats,
                kontor: user.kontor
            };
            this.createTimeBudget.emit(newTimeBudget);
        }
    }

    trackByMonthAndYear(_index: number, date: Date) {
        return date.getMonth().toString() + '' + date.getFullYear().toString();
    }

    trackByUserId(_index: number, user: IndexCard) {
        return user.cardId;
    }

    savePrisjustering(newValue: string) {
        const prisjustering = +newValue.split('.').join('').replace(',', '.');
        if (isNaN(prisjustering)) {
            this.alert.createAlert('Prisjustering skal være et tal!', 'danger');
            this.prisjusteringInput.nativeElement.focus();
            this.prisjusteringInput.nativeElement.select();
            return;
        }
        this.updateClientCaseV2.emit({sag: this.sag, partialSag: {prisjustering}});
    }
}
